import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Badge,
  Button,
  Empty,
  InputNumber,
  Select,
  Spin,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  CodeOutlined,
  HistoryOutlined,
  PlayCircleOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import Editor from '@monaco-editor/react';
import type * as Monaco from 'monaco-editor';
import { OPMApi } from '../../../../../../api/opmApi';
import type {
  FunctionLanguage,
  ProcessFuncCodeDetailDto,
  ProcessDefinStatus,
  ValidateProcessLogicResponse,
} from '../../../../../../api/opmApi';
import {
  LANGUAGE_OPTIONS,
  MONACO_LANG_MAP,
  RuntimeEnvSelector,
} from './shared';
import ExecutionHistoryPanel from './ExecutionHistoryPanel';
import ExecutionRunPanel from './ExecutionRunPanel';
import {
  generateFunctionSignature,
  generateTypeDefsFromDefinition,
  mergeSignatureWithSavedBody,
} from './signature-utils';
import {
  useMonacoLockedRanges,
  getFunctionSignatureLock,
} from '../../../../../../hooks/useMonacoLockedRanges';
import { useMonacoSyntaxValidation } from '../../../../../../hooks/useMonacoSyntaxValidation';
import type { ValidationResult as MonacoValidationResult } from '../../../../../../hooks/useMonacoSyntaxValidation';

interface UserDefinedFunctionConfigProps {
  ontologyId: number;
  processNodeId: string;
  definition: ProcessFuncCodeDetailDto | null;
  loading: boolean;
  onDefinitionUpdate: (definition: ProcessFuncCodeDetailDto) => void;
  /** 每次切换回执行逻辑 tab 时自增，触发重新拉取接口定义并重新生成代码 */
  refreshTrigger?: number;
}

const STATUS_MAP: Record<
  ProcessDefinStatus,
  { status: 'success' | 'error' | 'default'; text: string }
> = {
  VALID: { status: 'success', text: 'VALID' },
  INVALID: { status: 'error', text: 'INVALID' },
  DRAFT: { status: 'default', text: 'DRAFT' },
  DEPLOYED: { status: 'success', text: 'DEPLOYED' },
};

const FieldBlock: React.FC<{
  label: string;
  extra?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, extra, children }) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-4">
        <div className="text-xs font-medium text-gray-500">{label}</div>
        {extra}
      </div>
      <div>{children}</div>
    </div>
  );
};

const UserDefinedFunctionConfig: React.FC<UserDefinedFunctionConfigProps> = ({
  ontologyId,
  processNodeId,
  definition,
  loading,
  onDefinitionUpdate,
  refreshTrigger,
}) => {
  const [functionDefinition, setFunctionDefinition] =
    useState<ProcessFuncCodeDetailDto | null>(null);
  const [allSourceCode, setAllSourceCode] = useState<
    Partial<Record<FunctionLanguage, string>>
  >({});
  const [language, setLanguage] = useState<FunctionLanguage>('TYPESCRIPT');
  const [loadingFunctionDefinition, setLoadingFunctionDefinition] =
    useState(false);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] =
    useState<ValidateProcessLogicResponse | null>(null);
  const [typeDefs, setTypeDefs] = useState('');
  const [localTypeDefs, setLocalTypeDefs] = useState('');
  const [envId, setEnvId] = useState('default');
  const [showRunPanel, setShowRunPanel] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [timeoutSeconds, setTimeoutSeconds] = useState<number | null>(30);
  const [memoryLimitMb, setMemoryLimitMb] = useState<number | null>(512);
  const monacoRef = useRef<typeof Monaco | null>(null);
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);

  // 稳定引用：避免每次渲染都创建新 options 对象导致 Editor 组件不必要重渲染
  const editorOptions =
    useMemo<Monaco.editor.IStandaloneEditorConstructionOptions>(
      () => ({
        minimap: { enabled: false },
        fontSize: 13,
        lineNumbers: 'on',
        scrollBeyondLastLine: false,
        automaticLayout: true,
        tabSize: 2,
        formatOnPaste: true,
        suggestOnTriggerCharacters: true,
        quickSuggestions: true,
        acceptSuggestionOnCommitCharacter: false,
        parameterHints: { enabled: true },
        autoIndent: 'full',
        folding: true,
        wordWrap: 'on',
        readOnly: false,
        renderLineHighlight: 'line',
        scrollbar: {
          vertical: 'auto',
          horizontal: 'auto',
          verticalScrollbarSize: 12,
          horizontalScrollbarSize: 10,
        },
      }),
      []
    );

  const sourceCode = allSourceCode[language] || '';
  const currentDefinition = functionDefinition;
  const definitionStatus =
    currentDefinition?.status || definition?.status
      ? STATUS_MAP[
          (currentDefinition?.status ||
            definition?.status) as ProcessDefinStatus
        ] || STATUS_MAP.DRAFT
      : null;

  const loadFunctionDefinition = useCallback(async () => {
    if (!processNodeId || !ontologyId) {
      setFunctionDefinition(null);
      return;
    }

    setLoadingFunctionDefinition(true);
    try {
      // 切换到执行逻辑页或重新进入“用户定义函数”模式时，统一从新 function 接口拉取最新数据。
      const result = await OPMApi.getProcessFunctionDefinition(
        ontologyId,
        processNodeId
      );
      setFunctionDefinition(result);
      onDefinitionUpdate(result);
    } catch (error: any) {
      console.error('加载用户定义函数配置失败:', error);
      setFunctionDefinition(null);
      message.error(error.message || '加载用户定义函数配置失败');
    } finally {
      setLoadingFunctionDefinition(false);
    }
  }, [ontologyId, processNodeId, onDefinitionUpdate]);

  // 使用 Monaco 行锁定 Hook
  const { onEditorMount: onMonacoLockedMount } = useMonacoLockedRanges({
    calculator: getFunctionSignatureLock(language),
    onLockViolation: () => {
      // 使用单例模式：先销毁之前的提示，再显示新提示
      message.destroy();
      message.warning({
        content: '函数签名和类型定义区域已锁定，仅可编辑函数体内容',
        key: 'monaco-lock-warning', // 使用固定key确保单例
      });
    },
    violationDebounceMs: 500, // 500ms内只触发一次
    autoUpdate: true,
    code: sourceCode,
  });

  // 使用 Monaco 语法验证 Hook
  // 注意：传入 ref 对象本身（不是 .current），Hook 内部会在 validate 时实时读 .current
  const {
    validate: monacoValidate,
    validationResult: monacoValidationResult,
    isValidating: monacoIsValidating,
  } = useMonacoSyntaxValidation({
    editorRef,
    monacoRef,
    language: language === 'PYTHON' ? 'python' : 'typescript',
    autoValidate: false, // 手动触发验证
  });

  const applyMonacoTypeDefs = useCallback(
    (monaco: typeof Monaco, defsContent: string) => {
      if (!defsContent) return;

      try {
        // 这里沿用旧实现给 Monaco 注入过程签名，保证编辑器内联类型提示可用。
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tsLanguage = (monaco.languages as any).typescript;
        const tsDefaults = tsLanguage.typescriptDefaults;

        tsDefaults.setCompilerOptions({
          target: tsLanguage.ScriptTarget.ES2020,
          allowNonTsExtensions: true,
          moduleResolution: tsLanguage.ModuleResolutionKind.NodeJs,
          module: tsLanguage.ModuleKind.CommonJS,
          noEmit: true,
          esModuleInterop: true,
          strict: false,
          skipLibCheck: true,
          noImplicitAny: false,
        });

        tsDefaults.setDiagnosticsOptions({
          noSemanticValidation: false,
          noSyntaxValidation: false,
          diagnosticCodesToIgnore: [1108],
        });

        tsDefaults.setExtraLibs([]);
        tsDefaults.addExtraLib(defsContent, 'file:///process-types.d.ts');
      } catch (error) {
        console.error('加载类型定义失败:', error);
      }
    },
    []
  );

  useEffect(() => {
    if (!currentDefinition) {
      setValidationResult(null);
      return;
    }

    const nextLanguage = currentDefinition.language || 'TYPESCRIPT';
    const inputSchema = currentDefinition.inputSchema;
    const outputSchema = currentDefinition.outputSchema;
    const nextSignature = generateFunctionSignature({
      functionName:
        currentDefinition.codeName || currentDefinition.functionName,
      inputSchema,
      outputSchema,
      language: nextLanguage,
    });

    setLanguage(nextLanguage);
    setAllSourceCode(() => {
      const nextSourceCodeMap: Partial<Record<FunctionLanguage, string>> = {
        ...currentDefinition.sourceCode,
      };
      const savedCode = currentDefinition.sourceCode?.[nextLanguage];
      if (savedCode) {
        // 每次加载都用最新接口定义重新生成头部，仅保留用户编写的函数体，保证代码与 schema 同步
        nextSourceCodeMap[nextLanguage] = mergeSignatureWithSavedBody({
          newSignature: nextSignature,
          savedCode,
          language: nextLanguage,
        });
      } else {
        nextSourceCodeMap[nextLanguage] = nextSignature;
      }
      return nextSourceCodeMap;
    });

    setLocalTypeDefs(
      generateTypeDefsFromDefinition({
        functionName:
          currentDefinition.codeName || currentDefinition.functionName,
        inputSchema,
        outputSchema,
        processName: currentDefinition.processName,
      })
    );
    setTimeoutSeconds(currentDefinition.timeoutSeconds ?? 30);
    setMemoryLimitMb(currentDefinition.maxMemoryMb ?? 512);
  }, [currentDefinition]);

  useEffect(() => {
    loadFunctionDefinition();
    // refreshTrigger 每次自增时（从其他 tab 切回执行逻辑 tab），重新拉取最新接口定义
  }, [loadFunctionDefinition, refreshTrigger]);

  useEffect(() => {
    if (!processNodeId || !ontologyId) {
      setTypeDefs('');
      return;
    }

    let cancelled = false;

    OPMApi.getProcessTypeDefs(ontologyId, processNodeId)
      .then(result => {
        if (!cancelled) {
          setTypeDefs(result || '');
        }
      })
      .catch(error => {
        console.error('获取类型定义失败:', error);
        if (!cancelled) {
          setTypeDefs('');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [ontologyId, processNodeId]);

  useEffect(() => {
    const defs = sourceCode ? [typeDefs] : [localTypeDefs, typeDefs];
    const combinedDefs = defs.filter(Boolean).join('\n\n');
    if (combinedDefs && monacoRef.current) {
      applyMonacoTypeDefs(monacoRef.current, combinedDefs);
    }
  }, [applyMonacoTypeDefs, localTypeDefs, sourceCode, typeDefs]);

  const handleEditorDidMount = (
    editor: Monaco.editor.IStandaloneCodeEditor,
    monaco: typeof Monaco
  ) => {
    monacoRef.current = monaco;
    editorRef.current = editor;

    try {
      // 先调用锁定Hook的onMount
      onMonacoLockedMount(editor, monaco);

      editor.focus();
      editor.updateOptions({ readOnly: false });

      // 在当前弹窗场景下，空格键可能被外层交互误吞，这里显式回写空格，保证编辑器输入正常。
      editor.onKeyDown(event => {
        if (
          event.keyCode === monaco.KeyCode.Space &&
          !event.ctrlKey &&
          !event.metaKey &&
          !event.altKey
        ) {
          event.preventDefault();
          event.stopPropagation();
          editor.trigger('keyboard', 'type', { text: ' ' });
        }
      });

      const defs = sourceCode ? [typeDefs] : [localTypeDefs, typeDefs];
      const combinedDefs = defs.filter(Boolean).join('\n\n');
      if (combinedDefs) {
        window.setTimeout(() => applyMonacoTypeDefs(monaco, combinedDefs), 100);
      }
    } catch (error) {
      console.error('Monaco Editor 挂载回调执行出错:', error);
    }
  };

  const handleLanguageChange = (nextLanguage: FunctionLanguage) => {
    setLanguage(nextLanguage);
    setValidationResult(null);

    // 切换语言时始终重新生成头部（接口定义 + 函数签名），保留用户函数体
    const nextSignature = generateFunctionSignature({
      functionName:
        currentDefinition?.codeName || currentDefinition?.functionName,
      inputSchema: currentDefinition?.inputSchema,
      outputSchema: currentDefinition?.outputSchema,
      language: nextLanguage,
    });

    const savedCode = allSourceCode[nextLanguage];
    const nextCode = savedCode
      ? mergeSignatureWithSavedBody({
          newSignature: nextSignature,
          savedCode,
          language: nextLanguage,
        })
      : nextSignature;

    setAllSourceCode(previousState => ({
      ...previousState,
      [nextLanguage]: nextCode,
    }));
  };

  const handleSave = async () => {
    if (!processNodeId) return;

    setSaving(true);
    try {
      // 保存时一并回传运行约束字段，并保留服务端已有的 CPU 限制配置。
      const result = await OPMApi.saveProcessFunctionDefinition(
        ontologyId,
        processNodeId,
        {
          sourceCode: allSourceCode,
          language,
          functionName: currentDefinition?.functionName,
          cpuTimeSeconds: currentDefinition?.cpuTimeSeconds,
          timeoutSeconds: timeoutSeconds ?? undefined,
          maxMemoryMb: memoryLimitMb ?? undefined,
        }
      );
      setFunctionDefinition(result);
      onDefinitionUpdate(result);
      message.success('用户定义函数配置保存成功');
    } catch (error: any) {
      message.error(error.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    if (!editorRef.current || !monacoRef.current) {
      message.warning('编辑器未就绪');
      return;
    }

    setValidating(true);
    setValidationResult(null);

    try {
      // 使用 Monaco 本地验证
      const result = monacoValidate();

      if (!result) {
        message.warning('验证失败');
        setValidating(false);
        return;
      }

      // 转换为旧的 ValidateProcessLogicResponse 格式
      const validationResponse: ValidateProcessLogicResponse = {
        valid: result.valid,
        message: result.valid
          ? '代码语法检查通过'
          : `发现 ${result.errors.length} 个错误`,
        inputSchema: null,
        outputSchema: null,
      };

      setValidationResult(validationResponse);

      if (result.valid) {
        message.success({
          content: (
            <div>
              <div>代码语法检查通过</div>
              {result.warnings.length > 0 && (
                <div className="text-xs text-orange-500 mt-1">
                  但有 {result.warnings.length} 个警告
                </div>
              )}
            </div>
          ),
          duration: 2,
        });
      } else {
        message.error({
          content: (
            <div>
              <div>代码语法检查失败</div>
              <div className="text-xs mt-1">
                发现 {result.errors.length} 个错误
              </div>
            </div>
          ),
          duration: 3,
        });
      }
    } catch (error: any) {
      console.error('Validation error:', error);
      message.error('验证过程出错: ' + (error.message || '未知错误'));
    } finally {
      setValidating(false);
    }
  };

  if (!processNodeId) {
    return <Empty description="未找到 Process 节点信息" />;
  }

  if ((loading || loadingFunctionDefinition) && !currentDefinition) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Spin tip="正在加载执行逻辑配置..." />
      </div>
    );
  }

  if (!currentDefinition) {
    return (
      <Alert
        type="warning"
        showIcon
        message="暂未获取到用户定义函数配置"
        description="请先确认当前 Process 已创建定义，再进入执行逻辑页进行编辑。"
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-4">
        <div className="text-sm font-semibold text-gray-800">
          用户定义函数配置
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FieldBlock label="代码语言">
            <Select
              value={language}
              onChange={handleLanguageChange}
              options={LANGUAGE_OPTIONS}
            />
          </FieldBlock>

          <FieldBlock label="语法检查">
            <Button
              block
              icon={<CheckCircleOutlined />}
              onClick={handleValidate}
              loading={validating}
            >
              检查语法
            </Button>
          </FieldBlock>
        </div>

        {validationResult && (
          <Alert
            type={validationResult.valid ? 'success' : 'error'}
            showIcon
            message={validationResult.valid ? '验证通过' : '验证失败'}
            description={validationResult.message}
            closable
            onClose={() => setValidationResult(null)}
          />
        )}

        <FieldBlock label="代码编辑器">
          <div
            className="overflow-hidden rounded-lg border border-gray-200 bg-white"
            // 阻断 Monaco 内部键盘事件继续冒泡到画布层/页面层快捷键监听。
            onKeyDown={event => event.stopPropagation()}
            onKeyUp={event => event.stopPropagation()}
          >
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <CodeOutlined />
                  支持根据输入/输出定义自动生成函数签名
                </div>
                {definitionStatus && (
                  <Badge
                    status={definitionStatus.status}
                    text={definitionStatus.text}
                  />
                )}
              </div>
              <RuntimeEnvSelector envId={envId} onChange={setEnvId} />
            </div>

            <Editor
              height="360px"
              language={MONACO_LANG_MAP[language]}
              path={`process-function.${language === 'TYPESCRIPT' ? 'ts' : 'py'}`}
              value={sourceCode}
              onChange={value =>
                setAllSourceCode(previousState => ({
                  ...previousState,
                  [language]: value || '',
                }))
              }
              onMount={handleEditorDidMount}
              options={editorOptions}
              theme="vs-light"
            />
          </div>
        </FieldBlock>
      </section>

      <section className="flex flex-col gap-4">
        <div className="text-sm font-semibold text-gray-800">运行调试</div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FieldBlock
            label="超时时间（秒）"
            extra={
              <span className="text-[11px] text-gray-400">
                输入 -1 表示不限制
              </span>
            }
          >
            <InputNumber
              min={-1}
              precision={0}
              className="!w-full"
              value={timeoutSeconds}
              onChange={value => setTimeoutSeconds(value === 0 ? (timeoutSeconds !== null && timeoutSeconds > 0 ? -1 : 1) : value)}
            />
          </FieldBlock>

          <FieldBlock
            label="内存限制（MB）"
            extra={
              <span className="text-[11px] text-gray-400">
                输入 -1 表示不限制
              </span>
            }
          >
            <InputNumber
              min={-1}
              precision={0}
              step={128}
              className="!w-full"
              value={memoryLimitMb}
              onChange={value => setMemoryLimitMb(value === 0 ? (memoryLimitMb !== null && memoryLimitMb > 0 ? -1 : 1) : value)}
            />
          </FieldBlock>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={() => setShowRunPanel(previousState => !previousState)}
          >
            {showRunPanel ? '收起运行调试' : '打开运行调试'}
          </Button>
          <Button
            icon={<HistoryOutlined />}
            onClick={() => setShowHistory(true)}
          >
            查看日志
          </Button>
        </div>

        {showRunPanel ? (
          <div className="rounded-lg border border-gray-200 bg-white px-4 pb-4">
            <ExecutionRunPanel
              ontologyId={ontologyId}
              processNodeId={processNodeId}
              inputSchema={currentDefinition.inputSchema ?? null}
              outputSchema={currentDefinition.outputSchema ?? null}
              onClose={() => setShowRunPanel(false)}
              onExecutionComplete={() => undefined}
            />
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-400">
            点击“打开运行调试”后，可填写入参、执行并查看运行结果。
          </div>
        )}
      </section>

      <div className="flex items-center justify-end">
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
        >
          保存用户定义函数配置
        </Button>
      </div>

      <ExecutionHistoryPanel
        visible={showHistory}
        onClose={() => setShowHistory(false)}
        ontologyId={ontologyId}
        processNodeId={processNodeId}
      />
    </div>
  );
};

export default UserDefinedFunctionConfig;
